import * as amplitude from "@amplitude/analytics-browser"
import { keepSearchParams } from "@tastyworks/shared-browser-utils/url"
import { UTM_PARAM_KEYS } from "@tastyworks/shared-browser-utils/utm"

export interface AmplitudeInitOptions {
  serverZone?: "US" | "EU"
  instanceName?: string
  optOut?: boolean
  identityStorage?: amplitude.Types.IdentityStorageType
}

// Spelled out in full so a vendor default flip can't silently widen collection.
// Per-key rationale in the README.
const AUTOCAPTURE_CONFIG: amplitude.Types.AutocaptureOptions = {
  pageViews: { trackHistoryChanges: "all" },
  sessions: true,
  attribution: false,
  formInteractions: false,
  fileDownloads: false,
  elementInteractions: false,
  frustrationInteractions: false,
  networkTracking: false,
  webVitals: false,
}
const HTTP_URL_RE = /^https?:\/\//i

let instance: amplitude.Types.BrowserClient | undefined

// An empty apiKey is how an environment opts out.
export function initAmplitude(
  apiKey: string,
  options?: AmplitudeInitOptions
): void {
  if (typeof window === "undefined" || instance || !apiKey) {
    return
  }

  instance = amplitude.createInstance()
  instance
    .init(apiKey, {
      autocapture: AUTOCAPTURE_CONFIG,
      fetchRemoteConfig: false,
      ...options,
    })
    .promise.then(() => {
      instance!.add(makeStripQueryPlugin())
    })
    .catch((error) => {
      console.error("initAmplitude failed", error)
    })
}

export function setAmplitudeUserId(externalId: string | null): void {
  instance?.setUserId(externalId ?? undefined)
}

export function resetAmplitude(): void {
  instance?.reset()
}

export function setAmplitudeOptOut(optOut: boolean): void {
  instance?.setOptOut(optOut)
}

// AO2 routes carry tokens, secrets, and PII in query strings. Strip all params
// except UTM from URL event properties before events leave the browser.
function stripNonUtmParams(urlStr: string): string {
  try {
    const url = new URL(urlStr)
    keepSearchParams(url, [...UTM_PARAM_KEYS])
    return url.toString()
  } catch {
    return urlStr
  }
}

function makeStripQueryPlugin(): amplitude.Types.EnrichmentPlugin {
  return {
    name: "strip-non-utm-query-params",
    execute: async (event: amplitude.Types.Event) => {
      const props: Record<string, any> | undefined = event.event_properties
      if (!props) {
        return event
      }
      for (const [key, value] of Object.entries(props)) {
        if (typeof value === "string" && HTTP_URL_RE.test(value)) {
          props[key] = stripNonUtmParams(value)
        }
      }
      return event
    },
  }
}
